import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Search, 
  Download,
  CheckCircle2,
  ArrowUpDown,
  Loader2,
  KeyRound,
  Fingerprint
} from 'lucide-react';
import AdminLayout from '../AdminLayout';
import { getUsers, resetPassword } from '../../api/auth';
import { resetPalm } from '../../api/palm';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('username');
  const [sortOrder, setSortOrder] = useState('asc');
  const [resettingPassword, setResettingPassword] = useState(null);
  const [resettingPalm, setResettingPalm] = useState(null);

  // Fetch users from API
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getUsers();
        if (response.users) {
          setUsers(response.users);
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(err.message || 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      (user.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.phone || '').includes(searchTerm);
    return matchesSearch;
  });

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aVal = a[sortBy] || '';
    let bVal = b[sortBy] || '';
    
    if (sortBy === 'balance') {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    }
    
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleResetPassword = async (user) => {
    if (!window.confirm(`Reset password for ${user.username || user.email}?\n\nPassword will be reset to 'palmpay'`)) {
      return;
    }

    try {
      setResettingPassword(user.id);
      const response = await resetPassword({ userId: user.id });
      if (response.message) {
        alert(`✅ ${response.message}`);
        // Optionally refresh users list
        const updatedResponse = await getUsers();
        if (updatedResponse.users) {
          setUsers(updatedResponse.users);
        }
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      alert(`❌ Failed to reset password: ${err.message || 'Unknown error'}`);
    } finally {
      setResettingPassword(null);
    }
  };

  const handleResetPalm = async (user) => {
    if (!window.confirm(`Reset palm data for ${user.username || user.email}?\n\nThis will clear all palm biometric data.`)) {
      return;
    }

    try {
      setResettingPalm(user.id);
      const response = await resetPalm({ userId: user.id });
      if (response.success || response.message) {
        alert(`✅ ${response.message || 'Palm data reset successfully!'}`);
        // Refresh users list
        const updatedResponse = await getUsers();
        if (updatedResponse.users) {
          setUsers(updatedResponse.users);
        }
      }
    } catch (err) {
      console.error('Error resetting palm:', err);
      alert(`❌ Failed to reset palm: ${err.message || 'Unknown error'}`);
    } finally {
      setResettingPalm(null);
    }
  };

  const handleRegisterPalm = (user, hand) => {
    // Navigate to palm enrollment page with target user info
    navigate('/palm-enrollment', {
      state: {
        targetUserId: user.id,
        targetHand: hand.toLowerCase(),
        returnTo: '/admin/users'
      }
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-4 lg:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl lg:text-2xl font-bold text-gray-800">User Management</h2>
            <p className="text-xs lg:text-sm text-gray-600 mt-1">{sortedUsers.length} users found</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => alert('Download CSV feature')}
              variant="outline" 
              className="flex items-center gap-2 text-xs lg:text-sm h-9"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </Button>
            <Button className="bg-[#586BFF] flex items-center gap-2 text-xs lg:text-sm h-9">
              Add User
            </Button>
          </div>
        </div>

        {/* Search & Filters */}
        <Card className="p-4">
          <div className="space-y-3">
            {/* Search Bar */}
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 text-sm"
                />
              </div>
            </div>

          </div>
        </Card>

        {/* Users Table - Desktop */}
        <Card className="overflow-hidden hidden lg:block">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <button 
                      onClick={() => handleSort('username')}
                      className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase hover:text-gray-700"
                    >
                      User <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button 
                      onClick={() => handleSort('balance')}
                      className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase hover:text-gray-700"
                    >
                      Balance <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Left Palm</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Right Palm</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reset Password</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reset Palm</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-[#586BFF] mx-auto" />
                      <p className="text-sm text-gray-500 mt-2">Loading users...</p>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <p className="text-red-500 mb-2">Error loading users</p>
                      <p className="text-sm text-gray-500">{error}</p>
                    </td>
                  </tr>
                ) : sortedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <p className="text-gray-500">No users found</p>
                    </td>
                  </tr>
                ) : (
                  sortedUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#586BFF] to-[#9B62FF] flex items-center justify-center text-white font-bold text-sm">
                            {(user.username || user.email || 'U').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{user.username || user.email || 'Unknown'}</p>
                            <p className="text-xs text-gray-500">{user.email || ''}</p>
                            <p className="text-xs text-gray-400">{user.phone || 'No phone'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-gray-800">₹{(user.balance || 0).toLocaleString()}</p>
                      </td>
                      <td className="px-4 py-3">
                        {user.left_palm_id ? (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="text-xs font-medium">Active</span>
                          </div>
                        ) : (
                          <Button
                            onClick={() => handleRegisterPalm(user, 'Left')}
                            className="bg-blue-500 hover:bg-blue-600 h-7 text-xs px-3"
                          >
                            Register
                          </Button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {user.right_palm_id ? (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="text-xs font-medium">Active</span>
                          </div>
                        ) : (
                          <Button
                            onClick={() => handleRegisterPalm(user, 'Right')}
                            className="bg-blue-500 hover:bg-blue-600 h-7 text-xs px-3"
                          >
                            Register
                          </Button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          onClick={() => handleResetPassword(user)}
                          disabled={resettingPassword === user.id}
                          className="bg-amber-500 hover:bg-amber-600 h-7 text-xs px-3 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {resettingPassword === user.id ? (
                            <>
                              <Loader2 className="w-3 h-3 animate-spin" />
                              <span>Resetting...</span>
                            </>
                          ) : (
                            <>
                              <KeyRound className="w-3 h-3" />
                              <span>Reset</span>
                            </>
                          )}
                        </Button>
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          onClick={() => handleResetPalm(user)}
                          disabled={resettingPalm === user.id}
                          className="bg-red-500 hover:bg-red-600 h-7 text-xs px-3 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {resettingPalm === user.id ? (
                            <>
                              <Loader2 className="w-3 h-3 animate-spin" />
                              <span>Resetting...</span>
                            </>
                          ) : (
                            <>
                              <Fingerprint className="w-3 h-3" />
                              <span>Reset</span>
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Users Cards - Mobile */}
        <div className="lg:hidden space-y-3">
          {loading ? (
            <Card className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#586BFF] mx-auto" />
              <p className="text-sm text-gray-500 mt-2">Loading users...</p>
            </Card>
          ) : error ? (
            <Card className="p-6 text-center">
              <p className="text-red-500 mb-2">Error loading users</p>
              <p className="text-sm text-gray-500">{error}</p>
            </Card>
          ) : sortedUsers.length === 0 ? (
            <Card className="p-6 text-center">
              <p className="text-gray-500">No users found</p>
            </Card>
          ) : (
            sortedUsers.map((user) => (
              <Card key={user.id} className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#586BFF] to-[#9B62FF] flex items-center justify-center text-white font-bold">
                      {(user.username || user.email || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{user.username || user.email || 'Unknown'}</p>
                      <p className="text-xs text-gray-500">{user.email || ''}</p>
                      <p className="text-xs text-gray-400">{user.phone || 'No phone'}</p>
                    </div>
                  </div>
                </div>

                <div className="mb-3 text-sm">
                  <p className="text-xs text-gray-500">Balance</p>
                  <p className="font-semibold text-gray-800">₹{(user.balance || 0).toLocaleString()}</p>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Left Palm</p>
                    {user.left_palm_id ? (
                      <div className="flex items-center gap-1 text-green-600">
                        <CheckCircle2 className="w-3 h-3" />
                        <span className="text-xs">Active</span>
                      </div>
                    ) : (
                      <Button
                        onClick={() => handleRegisterPalm(user, 'Left')}
                        className="bg-blue-500 hover:bg-blue-600 w-full h-7 text-xs"
                      >
                        Register
                      </Button>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Right Palm</p>
                    {user.right_palm_id ? (
                      <div className="flex items-center gap-1 text-green-600">
                        <CheckCircle2 className="w-3 h-3" />
                        <span className="text-xs">Active</span>
                      </div>
                    ) : (
                      <Button
                        onClick={() => handleRegisterPalm(user, 'Right')}
                        className="bg-blue-500 hover:bg-blue-600 w-full h-7 text-xs"
                      >
                        Register
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 pt-3 border-t">
                  <Button 
                    onClick={() => handleResetPassword(user)}
                    disabled={resettingPassword === user.id}
                    variant="outline"
                    className="flex-1 text-xs h-8 flex items-center justify-center gap-1 disabled:opacity-50"
                  >
                    {resettingPassword === user.id ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span>Resetting...</span>
                      </>
                    ) : (
                      <>
                        <KeyRound className="w-3 h-3" />
                        <span>Reset Password</span>
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={() => handleResetPalm(user)}
                    disabled={resettingPalm === user.id}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-xs h-8 flex items-center justify-center gap-1 disabled:opacity-50"
                  >
                    {resettingPalm === user.id ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span>Resetting...</span>
                      </>
                    ) : (
                      <>
                        <Fingerprint className="w-3 h-3" />
                        <span>Reset Palm</span>
                      </>
                    )}
                  </Button>
                </div>
              </Card>
            ))
          )}
        </div>

      </div>
    </AdminLayout>
  );
};

export default UserManagement;